import { t } from "../i18n";
import type { StaticEvidenceProvider } from "../data/provider";
import type { ProfilePayload } from "../app/profile";
import { getSession } from "../app/auth";

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export function SkillsProfilePrint({
  profile,
  provider,
  createdAt,
}: {
  profile: ProfilePayload;
  provider: StaticEvidenceProvider;
  createdAt?: string;
}) {
  const career = provider.careerById(profile.careerId);
  const skills = provider.skillsById();
  const logo = getSession().logoUrl;
  const when = formatDate(createdAt ?? profile.completedAt);

  return (
    <article class="obs-print-sheet" aria-hidden="true">
      <header class="obs-print-brand">
        <div>
          {logo ? <img src={logo} alt="BOOSTSCIENCE" /> : <p class="obs-print-wordmark">BOOSTSCIENCE</p>}
          <p class="obs-eyebrow">{t("account.print.kicker")}</p>
        </div>
        <p class="obs-print-meta">
          {when}
          <br />
          {t("match.results.title")}
        </p>
      </header>
      <p class="obs-eyebrow">{t("match.results.pathway")}</p>
      <h2>{career?.title ?? profile.careerId}</h2>
      <div class="obs-results-score">
        <p class="obs-score">
          {Math.round(profile.matchPercent)}
          <span>%</span>
        </p>
        <div>
          <p class="obs-score-kicker">{t("match.results.score")}</p>
          <p class="obs-band">{t(`match.results.band.${profile.band}`)}</p>
          <p>{t(`match.results.lead.${profile.band}`)}</p>
        </div>
      </div>
      <div class="obs-cat-bars">
        {profile.categoryScores.map((cat) => (
          <div key={cat.groupId}>
            <p>
              {t(`group.${cat.groupId}`)}
              <b>{Math.round(cat.percent)}%</b>
            </p>
            <div class="obs-bar" aria-hidden="true">
              <span style={{ width: `${Math.round(cat.percent)}%` }} />
            </div>
          </div>
        ))}
      </div>
      <div class="obs-results-split">
        <div>
          <h3>{t("match.results.strengths")}</h3>
          <ul class="obs-list">
            {profile.strengths.map((id) => (
              <li key={id}>{skills.get(id)?.name ?? id}</li>
            ))}
          </ul>
        </div>
        <div>
          <h3>{t("match.results.priorities")}</h3>
          <ul class="obs-list">
            {profile.priorities.map((row) => (
              <li key={row.skillId}>{skills.get(row.skillId)?.name ?? row.skillId}</li>
            ))}
          </ul>
        </div>
      </div>
      {profile.learningResourceIds.length > 0 && (
        <>
          <h3>{t("match.results.path")}</h3>
          <ul class="obs-list">
            {profile.learningResourceIds.map((id) => {
              const r = provider.resourceById(id);
              return r ? <li key={id}>{r.title}</li> : null;
            })}
          </ul>
        </>
      )}
      <p class="obs-print-disclaimer">{t("account.print.disclaimer")}</p>
      <p class="obs-print-eu">{t("footer.notice")}</p>
    </article>
  );
}
